import { Currency } from 'dinero.js';
import {
  CouponLineItem,
  DamageWaiverLineItem,
  DeliveryLineItem,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../../../clients/quipli-service/financials/financials';
import { Configuration } from '../../../../clients/quipli-service/configuration/configuration';
import { Label } from '../../../../clients/quipli-service/label/label';
import { CustomerContext } from '../../../../context/customer-context';
import Dinero from 'dinero.js';
import { Location } from '../../../../clients/quipli-service/location/location';

export const useCartCalculations = ({
  summary,
  location,
  config,
  labels,
  customer,
}: {
  summary: FinancialSummary;
  location: Location;
  config: Configuration[];
  labels: Label[];
  customer: CustomerContext | undefined;
}) => {
  const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
  const delivery = orderDelivery
    ? orderDelivery.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      ) || Dinero({ amount: 0, currency: location.currency as Currency });
  const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
  const pickup = orderPickup
    ? orderPickup.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      ) || Dinero({ amount: 0, currency: location.currency as Currency });
  const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as
    | DamageWaiverLineItem
    | undefined;
  const coupon = summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon') as
    | CouponLineItem
    | undefined;
  const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
  const taxInclusive = config.find((c) => c.variable === 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1';
  const taxLabel = config.find((c) => c.variable === 'CONF_PRICE_TAX_LABEL')?.value || 'Tax';
  const locationFees = summary?.lineItems.filter((line) => line.type === 'Fee') as FeeLineItem[];
  const productFees = summary?.lineItems.reduce(
    (acc, line) => (line.type === 'ProductFee' && line.isSelected ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency: location.currency as Currency }),
  );
  const additionalDiscount = summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount;

  const orderSecurityDeposit = summary?.lineItems.find((line) => line.type === 'SecurityDeposit') as
    | SecurityDepositLineItem
    | undefined;
  const securityDepositPrice = orderSecurityDeposit
    ? orderSecurityDeposit.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductSecurityDeposit' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      );
  const checkoutText = labels.find((label) => label.key === 'L_CHECKOUT_TEXT')?.caption || '';
  const hidePrices = config.find((c) => c.variable === 'CONF_USER_UNAPPROVED_HIDE_PRICES')?.value === '1' && !customer;
  const displayTaxByJurisdiction =
    config.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1';

  const taxBreakdown = tax?.taxBreakdown || [];
  const zero = Dinero({ amount: 0, currency: location.currency as Currency });
  const deliveryTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Delivery')?.tax ?? zero);
  }, zero);
  const pickupTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Pickup')?.tax ?? zero);
  }, zero);
  const damageWaiverTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'DamageWaiver')?.tax ?? zero);
  }, zero);
  const additionalDiscountTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'AdditionalDiscount')?.tax ?? zero);
  }, zero);
  const couponTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Coupon')?.tax ?? zero);
  }, zero);
  const productFeesTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'ProductFee')?.tax ?? zero);
  }, zero);

  return {
    orderDelivery,
    delivery,
    pickup,
    orderPickup,
    damageWaiver,
    coupon,
    tax,
    taxInclusive,
    taxLabel,
    locationFees,
    productFees,
    additionalDiscount,
    orderSecurityDeposit,
    securityDepositPrice,
    checkoutText,
    hidePrices,
    displayTaxByJurisdiction,
    taxBreakdown,
    deliveryTax,
    pickupTax,
    damageWaiverTax,
    additionalDiscountTax,
    couponTax,
    productFeesTax,
  };
};
